import { WorkItemFactory } from 'common/workitems/workitem-factory';
import { WorkItemTypeValue, WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { WorkItemBase } from './models/workitem-base-model';
import { WorkItemComposeEmail } from './models/workitem-compose-email-model';
import { WorkItemHelper } from 'common/workitems/workitem-helper';

export class WorkItemAdapter {
  static createFromSearch(searchItem) {
    const workItem = this._convertSearchItemToWorkItem(searchItem);

    if (workItem.ItemType === WorkItemTypeValue.CHAT) {
      return WorkItemFactory.create(WorkItemTypes.CHAT, workItem);
    } else if (workItem.ItemType === WorkItemTypeValue.EMAIL) {
      return WorkItemFactory.create(WorkItemTypes.EMAIL, workItem);
    } else if (workItem.ItemType === WorkItemTypeValue.SMS) {
      return WorkItemFactory.create(WorkItemTypes.SMS, workItem);
    } else if (workItem.ItemType === WorkItemTypeValue.FACEBOOK) {
      return WorkItemFactory.create(WorkItemTypes.FACEBOOK, workItem);
    } else if (workItem.ItemType === WorkItemTypeValue.ASYNC_MESSAGE) {
      return WorkItemFactory.create(WorkItemTypes.ASYNC_MESSAGE, workItem);
    }
  }

  static createLensItem(workItem?) {
    if (workItem) {
      return WorkItemFactory.wrap(workItem);
    }

    const randomNumber = Math.floor(Math.random() * 10000);
    const defaultLensWorkItem = {
      id: randomNumber,
      ItemID: randomNumber,
      NewItem: true,
      type: WorkItemTypes.LENS,
      ItemType: WorkItemTypeValue.LENS,
      IsAnswered: false,
      keepAlive: true
    };

    return WorkItemFactory.create(WorkItemTypes.LENS, defaultLensWorkItem);
  }

  static createEmailComposeWorkItem(workItem?: WorkItemBase): WorkItemComposeEmail {
    if (workItem) {
      return <WorkItemComposeEmail>WorkItemFactory.wrap(workItem);
    }

    return <WorkItemComposeEmail>WorkItemFactory.create(WorkItemTypes.COMPOSE_EMAIL);
  }

  // eslint-disable-next-line max-statements
  static convertStaticQueryItemToWorkItem(staticQueryItem, workItemType) {
    const convertedItem = { ...staticQueryItem };

    this._convertProperty(convertedItem, 'accepted', 'Accepted');
    this._convertProperty(convertedItem, 'accountId', 'AccountID');
    this._convertProperty(convertedItem, 'answered', 'Answered');
    this._convertProperty(convertedItem, 'answeredByOperatorName', 'AnsweredByOperatorName');
    this._convertProperty(convertedItem, 'appliedRuleName', 'AppliedRuleName');
    this._convertProperty(convertedItem, 'chatId', 'ChatID');
    this._convertProperty(convertedItem, 'chatName', 'ChatName');
    this._convertProperty(convertedItem, 'chatType', 'ChatType');
    this._convertProperty(convertedItem, 'chatUrl', 'ChatUrl');
    this._convertProperty(convertedItem, 'chatWindowDefId', 'ChatWindowDefID');
    this._convertProperty(convertedItem, 'city', 'City');
    this._convertProperty(convertedItem, 'closed', 'Closed');
    this._convertProperty(convertedItem, 'closedByOperatorName', 'ClosedByOperatorName');
    this._convertProperty(convertedItem, 'created', 'Created');
    this._convertProperty(convertedItem, 'countryCode', 'CountryCode');
    this._convertProperty(convertedItem, 'customField1', 'CustomField1');
    this._convertProperty(convertedItem, 'customField2', 'CustomField2');
    this._convertProperty(convertedItem, 'customField1Id', 'CustomField1ID');
    this._convertProperty(convertedItem, 'customField2Id', 'CustomField2ID');
    this._convertProperty(convertedItem, 'customFieldValues', 'OperatorCustomFields');
    this._convertProperty(convertedItem, 'customVisitInfo', 'VisitInfo');
    this._convertProperty(convertedItem, 'customVisitRef', 'VisitRef');
    this._convertProperty(convertedItem, 'departmentId', 'DepartmentID');
    this._convertProperty(convertedItem, 'departmentName', 'DepartmentName');
    this._convertProperty(convertedItem, 'destinationEmailAddress', 'DestinationEmailAddress');
    this._convertProperty(convertedItem, 'fromEmailAccount', 'FromEmailAccount');
    this._convertProperty(convertedItem, 'emailCount', 'EmailCount');
    this._convertProperty(convertedItem, 'emailThreadId', 'EmailThreadID');
    this._convertProperty(convertedItem, 'emailThreadType', 'EmailThreadType');
    this._convertProperty(convertedItem, 'ended', 'Ended');
    this._convertProperty(convertedItem, 'firstIncomingArrived', 'FirstIncomingArrived');
    this._convertProperty(convertedItem, 'folderId', 'FolderID');
    this._convertProperty(convertedItem, 'folderName', 'FolderName');
    this._convertProperty(convertedItem, 'ip', 'IP');
    this._convertProperty(convertedItem, 'itemId', 'ItemID');
    this._convertProperty(convertedItem, 'languageCode', 'LanguageCode');
    this._convertProperty(convertedItem, 'lastACDStarted', 'LastACDStarted');
    this._convertProperty(convertedItem, 'lastAssignedAnswered', 'LastAssignedAnswered');
    this._convertProperty(convertedItem, 'lastAssignedByOperatorName', 'LastAssignedByOperatorName');
    this._convertProperty(convertedItem, 'lastEmailType', 'LastEmailType');
    this._convertProperty(convertedItem, 'lastIncomingArrived', 'LastIncomingArrived');
    this._convertProperty(convertedItem, 'opened', 'Opened');
    this._convertProperty(convertedItem, 'operatorId', 'OperatorID');
    this._convertProperty(convertedItem, 'operatorMessageCount', 'OperatorMessageCount');
    this._convertProperty(convertedItem, 'operatorName', 'OperatorName');
    this._convertProperty(convertedItem, 'queueTime', 'QueueTime');
    this._convertProperty(convertedItem, 'regionCode', 'RegionCode');
    this._convertProperty(convertedItem, 'started', 'Started');
    this._convertProperty(convertedItem, 'subject', 'Subject');
    this._convertProperty(convertedItem, 'surveyNps', 'SurveyNps');
    this._convertProperty(convertedItem, 'totalReassignCount', 'TotalReassignCount');
    this._convertProperty(convertedItem, 'updated', 'Updated');
    this._convertProperty(convertedItem, 'userCategoryId', 'UserCategoryID');
    this._convertProperty(convertedItem, 'userEmailAddress', 'UserEmailAddress');
    this._convertProperty(convertedItem, 'userEmailName', 'UserEmailName');
    this._convertProperty(convertedItem, 'visitorMessageCount', 'VisitorMessageCount');
    this._convertProperty(convertedItem, 'visitEmailAddress', 'VisitEmailAddress');
    this._convertProperty(convertedItem, 'visitorId', 'VisitorID');
    this._convertProperty(convertedItem, 'visitPhone', 'VisitPhone');
    this._convertProperty(convertedItem, 'websiteName', 'WebsiteName');
    this._convertProperty(convertedItem, 'windowClosed', 'WindowClosed');
    this._convertProperty(convertedItem, 'flagged', 'Flagged');
    this._convertProperty(convertedItem, 'lastMessageSent', 'LastMessageSent');
    this._convertProperty(convertedItem, 'textChatId', 'TextChatID');
    this._convertProperty(convertedItem, 'channelName', 'ChannelName');
    this._convertProperty(convertedItem, 'phoneNumber', 'PhoneNumber');
    this._convertProperty(convertedItem, 'email', 'Email');

    return WorkItemFactory.create(workItemType, convertedItem);
  }

  private static _convertSearchItemToWorkItem(searchItem) {
    const convertedSearchItem = { ...searchItem };
    convertedSearchItem.ItemID = convertedSearchItem.id;
    delete convertedSearchItem.id;

    convertedSearchItem.ItemType = WorkItemHelper.convertItemTypeToDbEnum(convertedSearchItem.type);
    delete convertedSearchItem.type;

    convertedSearchItem.Name = convertedSearchItem.name;
    delete convertedSearchItem.name;

    convertedSearchItem.Created = convertedSearchItem.created;
    delete convertedSearchItem.created;

    return convertedSearchItem;
  }

  private static _convertProperty(convertedItem, originalProperty: string, convertedProperty: string): void {
    if (typeof convertedItem[originalProperty] !== 'undefined') {
      convertedItem[convertedProperty] = convertedItem[originalProperty];
      delete convertedItem[originalProperty];
    }
  }
}
